import { useState, useEffect } from 'react'
import type { ChatPartner } from './ChatArea'
import api, { getFileUrl } from '../utils/api'
import StoriesSection from './StoriesSection'
import '../styles/sidebar.css'

interface Conversation {
  email: string
  prenom?: string
  nom?: string
  photoUrl?: string
  lastMessage?: string
  lastMessageDate?: string
  messageCount?: number
}

interface Group {
  id: string
  name?: string
  description?: string
  photoUrl?: string
  lastMessage?: string
  lastMessageDate?: string
  unreadCount?: number
}

interface SidebarProps {
  onContactsClick: () => void
  onTabChange: (tab: 'discussions' | 'stories' | 'groups') => void
  activeTab: 'discussions' | 'stories' | 'groups'
  onConversationSelect?: (conversation: ChatPartner) => void
  refreshTrigger?: number
}
 

export default function Sidebar({ onContactsClick, onTabChange, activeTab, onConversationSelect, refreshTrigger }: SidebarProps) {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [groups, setGroups] = useState<Group[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [groupError, setGroupError] = useState('')
  const [groupLoading, setGroupLoading] = useState(false)
  const userEmail = localStorage.getItem('userEmail')?.trim() || ''

  const getConversationTitle = (conversation: Conversation) => {
    const fullName = [conversation.prenom, conversation.nom].filter(Boolean).join(' ').trim()
    return fullName || conversation.email
  }

  const formatSnippet = (text?: string) => {
    if (!text) return ''
    return text.length > 60 ? `${text.slice(0, 60)}…` : text
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return ''
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  useEffect(() => {
    const loadConversations = async () => {
      if (!userEmail || activeTab !== 'discussions') return
      setLoading(true)
      setError('')

      try {
        console.log('🔎 Loading recent conversations for', userEmail.toLowerCase())
        const response = await api.get('/api/recent-conversations', {
          params: { email: userEmail.toLowerCase() }
        })
        console.log('🔎 recent-conversations response:', response.data)
        if (response.data && !Array.isArray(response.data)) {
          const apiError = (response.data as any).error
          if (apiError) {
            throw new Error(apiError)
          }
        }
        const data = Array.isArray(response.data) ? response.data : []
        const sorted = data.sort((a, b) => {
          const dateA = new Date(a.lastMessageDate || '').getTime()
          const dateB = new Date(b.lastMessageDate || '').getTime()
          return dateB - dateA
        })
        setConversations(sorted)
      } catch (err: any) {
        console.error('Erreur lors du chargement des discussions:', err)
        setError(err.response?.data?.error || err.message || 'Erreur lors du chargement des discussions')
      } finally {
        setLoading(false)
      }
    }

    loadConversations()
  }, [activeTab, userEmail, refreshTrigger])

  useEffect(() => {
    const loadGroups = async () => {
      if (!userEmail || activeTab !== 'groups') return
      setGroupLoading(true)
      setGroupError('')

      try {
        const response = await api.get('/api/groups', {
          params: { email: userEmail.toLowerCase() }
        })

        const data = Array.isArray(response.data) ? response.data : []
        setGroups(data)
      } catch (err: any) {
        console.error('Erreur lors du chargement des groupes :', err)
        setGroupError(err.response?.data?.error || err.message || 'Erreur lors du chargement des groupes')
      } finally {
        setGroupLoading(false)
      }
    }

    loadGroups()
  }, [activeTab, userEmail, refreshTrigger])

  const handleConversationClick = (conversation: any) => {
    if (conversation.type === 'group') {
      onConversationSelect?.({
        type: 'group',
        id: conversation.id,
        name: conversation.name,
        description: conversation.description,
        photoUrl: conversation.photoUrl
      })
    } else {
      onConversationSelect?.({
        type: 'user',
        id: conversation.email,
        email: conversation.email,
        prenom: conversation.prenom,
        nom: conversation.nom,
        statut: 'online',
        photoUrl: conversation.photoUrl
      })
    }
  }

  return (
    <div className="sidebar">
      {/* Bouton + toujours visible */}
      <button 
        className="btn-add-contact"
        onClick={onContactsClick}
        title="Ajouter un contact"
      >
        +
      </button>

      {/* Tabs */}
      <div className="sidebar-tabs">
        <button
          className={`tab ${activeTab === 'discussions' ? 'active' : ''}`}
          onClick={() => onTabChange('discussions')}
        >
          💬 Discussions
        </button>
        <button
          className={`tab ${activeTab === 'stories' ? 'active' : ''}`}
          onClick={() => onTabChange('stories')}
        >
          📖 Stories
        </button>
        <button
          className={`tab ${activeTab === 'groups' ? 'active' : ''}`}
          onClick={() => onTabChange('groups')}
        >
          👥 Groupes
        </button>
      </div>

      {/* Content Area */}
      <div className="sidebar-content">
        {activeTab === 'discussions' && (
          <div className="list-container">
            {loading ? (
              <p className="empty-state">Chargement des discussions...</p>
            ) : error ? (
              <p className="empty-state">{error}</p>
            ) : conversations.length === 0 ? (
              <p className="empty-state">Aucune discussion pour le moment</p>
            ) : (
              <div className="conversation-list">
                {conversations.map((conversation) => (
                  <button
                    key={conversation.email}
                    className="conversation-item"
                    onClick={() => handleConversationClick(conversation)}
                    type="button"
                  >
                    <div className="conversation-avatar">
                      {conversation.photoUrl ? (
                        <img src={getFileUrl(conversation.photoUrl)} alt={conversation.prenom || conversation.email} onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/40?text=User'; }} />
                      ) : (
                        <span>{(conversation.prenom?.[0] || conversation.email[0]).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="conversation-info">
                      <div className="conversation-title">
                        {getConversationTitle(conversation)}
                      </div>
                      <div className="conversation-message">
                        {formatSnippet(conversation.lastMessage)}
                      </div>
                    </div>
                    <div className="conversation-meta">
                      <span className="conversation-date">
                        {conversation.lastMessageDate && formatDate(conversation.lastMessageDate)}
                      </span>
                      {(conversation.messageCount ?? 0) > 0 ? (
                        <span className="conversation-badge">{conversation.messageCount}</span>
                      ) : null}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {activeTab === 'stories' && (
          <StoriesSection userEmail={userEmail} />
        )}
        {activeTab === 'groups' && (
          <div className="list-container">
            {groupLoading ? (
              <p className="empty-state">Chargement des groupes...</p>
            ) : groupError ? (
              <p className="empty-state">{groupError}</p>
            ) : groups.length === 0 ? (
              <p className="empty-state">Aucun groupe pour le moment</p>
            ) : (
              <div className="conversation-list">
                {groups.map((group) => (
                  <button
                    key={group.id}
                    className="conversation-item"
                    onClick={() => handleConversationClick({
                      ...group,
                      type: 'group'
                    })}
                    type="button"
                  >
                    <div className="conversation-avatar">
                      {group.photoUrl ? (
                        <img src={getFileUrl(group.photoUrl)} alt={group.name} onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/40?text=Group'; }} />
                      ) : (
                        <span>{(group.name?.[0] || 'G').toUpperCase()}</span>
                      )}
                    </div>
                    <div className="conversation-info">
                      <div className="conversation-title">{group.name}</div>
                      <div className="conversation-message">{formatSnippet(group.lastMessage)}</div>
                    </div>
                    <div className="conversation-meta">
                      <span className="conversation-date">{formatDate(group.lastMessageDate)}</span>
                      {(group.unreadCount ?? 0) > 0 ? (
                        <span className="conversation-badge">{group.unreadCount}</span>
                      ) : null}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
